<template>
  <div class="vendors-items-main vendors-items-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Vendor', 'Items']" />

    <div class="glass-tab-view glass-tab-view--flush vendors-items-glass-shell">
      <div class="glass-tab-view__content vendors-items-tab-content">
        <div class="vendors-items-toolbar vendors-items-toolbar-glass glass-tab-panel--nested">
          <div class="vendors-items-list-header-actions">
            <SecondaryButton
              label="Export to CSV"
              icon="bi bi-download"
              @click="handleExportCSV"
            />
            <PrimaryButton
              label="Add a New Item"
              icon="bi bi-plus-lg"
              @click="handleAddNewItem"
            />
          </div>
          <div class="vendors-items-list-filters vendors-items-list-filters-row">
            <div class="vendors-items-list-filter-field vendors-items-list-filter-field--status">
              <LabelDropdown
                label="Status"
                select-id="vendor-items-list-status"
                v-model="currentViewStatus"
                inline
                flex
                :searchable="false"
                :options="statusFilterDropdownOptions"
              />
            </div>
            <div class="vendors-items-list-filter-field vendors-items-list-filter-field--bill-status">
              <LabelDropdown
                label="Bill Status"
                select-id="vendor-items-list-bill-status"
                v-model="currentBillStatus"
                inline
                flex
                :searchable="false"
                :options="billStatusFilterDropdownOptions"
              />
            </div>
          </div>
        </div>

        <div class="vendor-items-list-card vendors-items-main-card glass-tab-panel--nested">
          <div class="vendors-items-table-section consultant-glass-table-section">
            <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table vendors-items-list-table">
                  <thead>
                    <tr>
                      <th>Active</th>
                      <th>Item Name</th>
                      <th>Description</th>
                      <th>Type</th>
                      <th>Purchase Price</th>
                      <th>Bill Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="filteredItems.length === 0">
                      <td colspan="6" class="consultant-glass-table-no-records">No records to show</td>
                    </tr>
                    <tr
                      v-for="(item, idx) in filteredItems"
                      :key="idx"
                      class="consultant-glass-table-row"
                    >
                      <td><input type="checkbox" :checked="item.status === 'active'" disabled /></td>
                      <td>{{ item.name }}</td>
                      <td>{{ item.description }}</td>
                      <td>{{ item.type }}</td>
                      <td
                        class="vendors-items-list-amount-cell"
                        :class="{ 'vendors-items-list-amount-cell--empty': !formatPurchasePrice(item.purchasePrice) }"
                      >{{ formatPurchasePrice(item.purchasePrice) || '-' }}</td>
                      <td>
                        <span :class="billStatusClass(item.billStatus)">
                          {{ item.billStatus || 'Available' }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="vendor-items-list-pagination-wrapper">
            <PaginationComponent
              select-id="vendor-items-list-items-per-page"
              :current-page="currentPage"
              :total-items="totalFilteredItems"
              :items-per-page="itemsPerPage"
              item-label="items"
              inline
              :searchable="false"
              :show-items-per-page="true"
              @page-change="handlePageChange"
              @items-per-page-change="handleItemsPerPageChange"
            />
          </div>
        </div>
      </div>
    </div>

    <SelectPurchaseItem
      v-if="showSelectPurchaseItemModal"
      v-model="selectedItemType"
      @ok="handleSelectItemTypeOk"
      @close="handleSelectItemTypeCancel"
    />
    <VendorDetailsSearch v-if="showSearchModal" @close="showSearchModal = false" />
  </div>
</template>

<script>
import '../styles/VendorItemsList.css';
import VendorDetailsSearch from '@/modules/vendor/views/VendorDetailsSearch.vue';
import SelectPurchaseItem from '@/modules/vendor/views/SelectPurchaseItem.vue';
import ApiService from '@/services/api.service.js';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import PaginationComponent from '@/components/PaginationComponent.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'VendorItemsList',
  components: {
    VendorDetailsSearch,
    SelectPurchaseItem,
    BrucdcumHeader,
    PaginationComponent,
    LabelDropdown,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      showSelectPurchaseItemModal: false,
      showSearchModal: false,
      selectedItemType: 'Product',
      currentViewStatus: 'active',
      currentBillStatus: 'all',
      items: [],
      currentPage: 1,
      itemsPerPage: 10,
    };
  },
  async created() {
    await this.fetchItems();
  },
  computed: {
    statusFilterDropdownOptions() {
      return [
        { value: 'all', label: 'All' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ];
    },
    billStatusFilterDropdownOptions() {
      return [
        { value: 'all', label: 'All' },
        { value: 'Available', label: 'Available' },
        { value: 'Bill Created', label: 'Bill Created' },
        { value: 'Bill Paid', label: 'Bill Paid' },
        { value: 'Expense Paid', label: 'Expense Paid' },
      ];
    },
    filteredItems() {
      let filtered = this.items;

      if (this.currentViewStatus !== 'all') {
        filtered = filtered.filter((item) => item.status === this.currentViewStatus);
      }

      if (this.currentBillStatus !== 'all') {
        filtered = filtered.filter((item) => item.billStatus === this.currentBillStatus);
      }

      const start = (this.currentPage - 1) * this.itemsPerPage;
      return filtered.slice(start, start + this.itemsPerPage);
    },
    totalFilteredItems() {
      let filtered = this.items;

      if (this.currentViewStatus !== 'all') {
        filtered = filtered.filter((item) => item.status === this.currentViewStatus);
      }

      if (this.currentBillStatus !== 'all') {
        filtered = filtered.filter((item) => item.billStatus === this.currentBillStatus);
      }

      return filtered.length;
    },
  },
  watch: {
    currentViewStatus() {
      this.currentPage = 1;
    },
    currentBillStatus() {
      this.currentPage = 1;
    },
  },
  methods: {
    billStatusClass(billStatus) {
      const slug = (billStatus || 'Available').toLowerCase().replace(/\s+/g, '-');
      return ['vendors-items-list-bill-status', `vendors-items-list-bill-status-${slug}`];
    },
    formatPurchasePrice(amount) {
      if (amount === null || amount === undefined || amount === '' || Number.isNaN(Number(amount))) {
        return '';
      }
      const n = Number(amount);
      if (n === 0) return '';
      return `$${n.toFixed(2)}`;
    },
    async fetchItems() {
      try {
        const companyId = this.$route.params.id;
        const productRes = await ApiService.get(`/vendor/product-items/company/${companyId}`);
        const productItems = (productRes.data || []).map((item) => ({
          status: item.active ? 'active' : 'inactive',
          name: item.productName,
          description: item.purchaseDescription || item.salesDescription || '',
          type: 'Product',
          purchasePrice: item.purchasePrice,
          billStatus: item.billStatus || 'Available',
        }));

        const serviceRes = await ApiService.get(`/vendor/service-items/company/${companyId}`);
        const serviceItems = (serviceRes.data || []).map((item) => ({
          status: item.active ? 'active' : 'inactive',
          name: item.serviceName,
          description: item.purchaseDescription || item.salesDescription || '',
          type: 'Service',
          purchasePrice: item.purchasePrice,
          billStatus: item.billStatus || 'Available',
        }));

        this.items = [...productItems, ...serviceItems];
      } catch (e) {
        this.items = [];
      }
    },
    handleAddNewItem() {
      this.showSelectPurchaseItemModal = true;
    },
    handleExportCSV() {
      this.$toast && this.$toast.info('Export to CSV coming soon!');
    },
    handleSelectItemTypeOk(selectedType) {
      this.selectedItemType = selectedType;
      this.showSelectPurchaseItemModal = false;
      const companyId = this.$route.params.id;

      switch (selectedType) {
        case 'Service Item':
          this.$router.push({
            name: 'VendorServiceItem',
            params: { id: companyId },
          });
          break;
        case 'Product Item':
          this.$router.push({
            name: 'VendorProductItem',
            params: { id: companyId },
          });
          break;
        default:
          this.$router.push({
            name: 'VendorServiceItem',
            params: { id: companyId },
          });
      }
    },
    handleSelectItemTypeCancel() {
      this.showSelectPurchaseItemModal = false;
    },
    handlePageChange(page) {
      this.currentPage = page;
    },
    handleItemsPerPageChange(itemsPerPage) {
      this.itemsPerPage = itemsPerPage;
      this.currentPage = 1;
    },
  },
};
</script>
